import { Injectable } from "@angular/core";
import {AccountService, AuthorizationService, SubscriptionService} from "../api/services";
import { Observable, of, forkJoin, BehaviorSubject } from "rxjs";
import { map, filter } from "rxjs/operators";
import { RootInjectorGuard } from "./root-injector-guard";
import { UserState } from "../api/models";
@Injectable({
  providedIn: "root",
})
export class GlobalApp extends RootInjectorGuard {
  private isFullScreenMode$ = new BehaviorSubject<boolean>(false);
  private loggedIn$ = new BehaviorSubject<boolean>(false);
  private admin$ = new BehaviorSubject<boolean>(false);
  private userName$ = new BehaviorSubject<string>("");
  private userState$ = new BehaviorSubject<UserState>({
    invoiceManagerOnly: false,
    reportOnly: false,
    userManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    xmlas: [],
    preFilteredInteractives: [],
  });
  private loadingStatus$ = new BehaviorSubject<LoadingStatus>({
    isLoading: false,
    loadingMessage: "",
  });
  private loadingMessages: string[] = [];

  get isReportExport() {
    return this.isFullScreenMode$.asObservable();
  }

  get loadingStatus() {
    return this.loadingStatus$.asObservable();
  }

  get userState() {
    return this.userState$.asObservable();
  }

  get isLoggedIn() {
    return this.loggedIn$.asObservable();
  }

  get isAdmin() {
    return this.admin$.asObservable();
  }

  get currentUserName() {
    return this.userName$.asObservable();
  }

  constructor(
    private authService: AuthorizationService,
    private accountService: AccountService
  ) {
    super(GlobalApp);

    this.loggedIn$.next(this.isValidLogin());
    this.admin$.next(this.isValidAdmin());
    this.userName$.next(localStorage.getItem("emailAddress"));
  }

  public logIn(
    id: string,
    authToken: string,
    isAdmin: boolean,
    emailAddress: string
  ) {
    localStorage.setItem("currentAuthToken", authToken);
    localStorage.setItem("id", id);
    localStorage.setItem("isAdmin", isAdmin ? "t" : "f");
    localStorage.setItem("emailAddress", emailAddress);

    this.loggedIn$.next(this.isValidLogin());
    this.admin$.next(this.isValidAdmin());
    this.userName$.next(localStorage.getItem("emailAddress"));
  }

  public logOut() {

    this.accountService.accountLogUserLogout().subscribe(result => {

    });

    localStorage.removeItem("currentAuthToken");
    localStorage.removeItem("id");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("emailAddress");

    this.loggedIn$.next(this.isValidLogin());
    this.admin$.next(this.isValidAdmin());
    this.userName$.next("");

    this.userState$.next({
      invoiceManagerOnly: false,
      reportOnly: false,
      userManagerOnly: false,
      interactives: [],
      modifiedInteractives: [],
      xmlas: [],
    });
  }

  public getId(): string {
    return localStorage.getItem("id");
  }

  public getAuthToken(): string {
    return localStorage.getItem("currentAuthToken");
  }

  public isValidLogin(): boolean {
    const authToken = localStorage.getItem("currentAuthToken");
    if (authToken !== null) return true;
    return false;
  }

  public isValidAdmin(): boolean {
    const adminStatus = localStorage.getItem("isAdmin");

    return adminStatus !== null && adminStatus === "t";
  }

  public isExistingEmail(emailAddress: string): Observable<boolean> {
    return this.authService.authorizationIsValidEmail(emailAddress).pipe();
  }

  public getAuthorizationStatus(): Observable<AuthorizationStatus> {
    const status: AuthorizationStatus = {
      isValid: false,
      userId: "",
      authToken: "",
      isAdmin: false,
    };

    return forkJoin(
      this.authService.authorizationIsValidLogin(),
      this.authService.authorizationIsAdmin()
    ).pipe(
      map((data) => {
        status.isValid = data[0] as boolean;
        if (status.isValid) {
          status.userId = localStorage.getItem("id");
          status.authToken = localStorage.getItem("currentAuthToken");
          status.isAdmin = data[1] as boolean;
        }

        return status;
      })
    );
  }

  public setFullScreenMode() {
    this.isFullScreenMode$.next(true);
  }

  public setUserState(userState: UserState) {
    this.userState$.next(userState);
  }

  public refreshUserState(subscriptionService: SubscriptionService) {
    subscriptionService
      .subscriptionGetUserSubscriptionState()
      .subscribe((userState) => {
        this.setUserState(userState);
      });
  }

  public pushLoading(loadingMessage: string) {
    this.loadingMessages.push(loadingMessage);
    this.loadingStatus$.next({ isLoading: true, loadingMessage });
  }

  public popLoading() {
    if (this.loadingMessages.length > 0) {
      this.loadingMessages.splice(this.loadingMessages.length - 1, 1);
      if (this.loadingMessages.length > 0) {
        this.loadingStatus$.next({
          isLoading: true,
          loadingMessage: this.loadingMessages[this.loadingMessages.length - 1],
        });
      } else {
        this.loadingStatus$.next({ isLoading: false, loadingMessage: "" });
      }
    }
  }
}

export interface AuthorizationStatus {
  isValid: boolean;
  userId: string;
  authToken: string;
  isAdmin: boolean;
}

export interface LoadingStatus {
  isLoading: boolean;
  loadingMessage: string;
}
